import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Container,
  CircularProgress,
  Fade,
  Rating,
  Checkbox,
  FormControlLabel,
  Paper,
  Card,
  CardMedia,
  Stack,
  Divider,
  Alert
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  LocationOn as LocationOnIcon,
  Star as StarIcon,
  Store as StoreIcon, 
  Home as HomeIcon 
} from "@mui/icons-material";

import axiosClient from "axiosClient"; 
import sixthBg from '../../../assets/centrum_assets/design/index/sixthBG.png';

const Feedback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // --- STATE ---
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [station, setStation] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contactNumber: "",
    message: "",
    consent: false,
  });

  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");

  const [ratings, setRatings] = useState({
    experience: 0,
    facilities: 0,
    service: 0,
    cleanliness: 0,
  });
// --- 1. FETCH STATION ---
  useEffect(() => {
    const dataParam = searchParams.get("data");
    
    if (!dataParam) {
      setFetchError("Invalid QR Code: No station data found.");
      setLoading(false);
      return;
    }

    try {
      const decodedString = atob(dataParam);
      // The QR string format is: "ID|LocationName"
      // Example 1: "12345|Manila Station"
      // Example 2: "NO_ID|Cavite Station"
      const parts = decodedString.split("|");
      
      const gpsId = parts[0];
      const locationName = parts[1] || ""; 

      if (!gpsId) throw new Error("Missing ID");
      
      // Pass both values to the fetch function
      fetchStationDetails(gpsId, locationName);
      
    } catch (err) {
      console.error(err);
      setFetchError("This QR code appears to be damaged or invalid.");
      setLoading(false);
    }
  }, [searchParams]);


const fetchStationDetails = async (gpsId, locationName) => {
    try {
      // 1. Parallel Fetch: Get the Station Data AND the Default Images
      const [stationRes, defaultsRes] = await Promise.all([
        axiosClient.get(`/feedback/lookup`, {
          params: {
            gps_id: gpsId,
            location: locationName
          }
        }),
        // Fetch defaults 
        axiosClient.get("/station-lists/settings/defaults").catch(() => ({ data: {} }))
      ]);
      
      const rawStation = stationRes.data.data;
      const defaultsData = defaultsRes.data?.data || {};

      // 2. Logic to Resolve Defaults 
      const globalDefaultMain = defaultsData.main_image || defaultsData.mainImage || null;
      const globalDefaultThumb = defaultsData.thumbnail || null;

      const hasCustomThumbnail = rawStation.thumbnail && rawStation.thumbnail !== "";
      const finalThumbnail = hasCustomThumbnail ? rawStation.thumbnail : globalDefaultThumb;

      const hasCustomMain = rawStation.mainImage && rawStation.mainImage !== "";
      const finalMain = hasCustomMain ? rawStation.mainImage : globalDefaultMain;

      // 3. Create the Merged Object
      const mergedStation = {
        ...rawStation,
        thumbnail: finalThumbnail,
        mainImage: finalMain,
        displayImage: finalThumbnail || finalMain
      };

      setStation(mergedStation);
      setLoading(false);
    } catch (err) {
      console.error("Lookup failed:", err);
      setFetchError("We could not find this station in our system.");
      setLoading(false);
    }
  };

  // --- VALIDATION LOGIC ---
  const validateField = (name, value) => {
    let errorMsg = null;
    switch (name) {
      case "name":
        if (!value.trim()) errorMsg = "Full Name is required.";
        break;
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) errorMsg = "Email is required.";
        else if (!emailRegex.test(value)) errorMsg = "Invalid email address.";
        break;
      case "contactNumber":
        const phoneRegex = /^\d+$/;
        if (!value.trim()) errorMsg = "Contact Number is required.";
        else if (!phoneRegex.test(value)) errorMsg = "Digits only please.";
        else if (value.length < 7) errorMsg = "Number is too short.";
        break;
      case "message":
        if (!value.trim()) errorMsg = "Message cannot be empty.";
        break;
      default:
        break;
    }
    return errorMsg;
  };

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    if (name === 'consent' && checked) setGeneralError("");
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const errorMsg = validateField(name, value);
    if (errorMsg) setErrors(prev => ({ ...prev, [name]: errorMsg }));
  };

  const handleRatingChange = (name, value) => {
    setRatings(prev => ({ ...prev, [name]: value || 0 }));
    if (generalError && value > 0) setGeneralError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");

    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'consent') {
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const hasMissingRating = Object.values(ratings).some(val => val === 0);
    if (hasMissingRating) {
      setGeneralError("Please rate all categories.");
      return;
    }

    if (!formData.consent) {
      setGeneralError("Please agree to the privacy policy.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        station_list_id: station.id,
        sender_name: formData.name,
        sender_email: formData.email,
        contact_number: formData.contactNumber,
        message: formData.message,
        rating_experience: ratings.experience,
        rating_facilities: ratings.facilities,
        rating_service: ratings.service,
        rating_cleanliness: ratings.cleanliness,
        consent: formData.consent,
      };

      await axiosClient.post("/feedback", payload);
      setSuccess(true);
      // Scroll handled by useEffect now
    } catch (err) {
      console.error(err);
      setGeneralError("Submission failed. Check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewStation = () => {
    navigate("/station_details", { 
      state: { 
        station: station, 
        currentStation: station 
      } 
    });
  };

  // --- RENDER HELPERS ---
  const renderTextField = (name, label, icon, multiline = false) => (
    <TextField
      name={name}
      label={label}
      fullWidth
      required
      multiline={multiline}
      rows={multiline ? 3 : 1}
      value={formData[name]}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!errors[name]}
      helperText={name === "message" ? `${formData.message.length}/500` : errors[name]} 
      size="medium"
      inputProps={
        name === "message" 
        ? { maxLength: 500, style: { fontSize: '1rem' } } 
        : { style: { fontSize: '1rem' } }
      }
      InputProps={{
        startAdornment: icon ? <Box sx={{ mr: 1.5, color: 'text.secondary', display: 'flex' }}>{icon}</Box> : null,
      }}
      sx={{
        "& .MuiInputBase-root": {
          backgroundColor: "#f8f9fa",
          borderRadius: "12px",
          padding: multiline ? '12px' : null,
        },
        "& .MuiFormHelperText-root": {
          margin: 0,
          marginTop: '4px',
          textAlign: name === "message" ? "right" : "left"
        }
      }}
    />
  );

  const renderRating = (label, name) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
      <Typography variant="body2" sx={{ color: '#555', mb: 0.5, fontWeight: 700, fontSize: '0.9rem' }}>
        {label}
      </Typography>
      <Rating
        name={name}
        value={ratings[name]}
        onChange={(_, val) => handleRatingChange(name, val)}
        size="large"
        emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
        sx={{ 
            color: "#ffb400",
            fontSize: "2.2rem" 
        }}
      />
    </Box>
  );

  if (loading) return <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress sx={{ color: '#00b5e2' }} /></Box>;
  if (fetchError) return <Container maxWidth="sm" sx={{ mt: 10 }}><Alert severity="error">{fetchError}</Alert></Container>;
  
  // --- SUCCESS VIEW ---
  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundImage: `url(${sixthBg})`, backgroundSize: 'cover', px: 2 }}>
        <Fade in={true} timeout={1000}>
          <Paper elevation={3} sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', borderRadius: 6, maxWidth: 600, width: '100%', mx: 'auto' }}>
            
            <CheckCircleIcon sx={{ fontSize: 80, color: '#8BC34A', mb: 2 }} />
            
            <Typography variant="h3" fontWeight="bold" sx={{ color: '#0a1a3b', mb: 1 }}>
              Thank You!
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Your feedback helps us improve our services at
              <br />
              <Typography component="span" variant="h5" fontWeight="800" sx={{ color: '#00b5e2', display: 'block', mt: 1 }}>
                {station?.location}
              </Typography>
            </Typography>

            <Divider sx={{ my: 4, borderColor: '#eee' }} />

            <Typography variant="h6" fontWeight="600" sx={{ color: '#555', mb: 3 }}>
              Would you like to know more about the station you rated?
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              
              <Button 
                onClick={handleViewStation}
                variant="contained" 
                size="large" 
                startIcon={<StoreIcon />}
                sx={{ 
                  bgcolor: '#00b5e2', 
                  borderRadius: 50, 
                  px: 4, 
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0, 181, 226, 0.4)',
                  '&:hover': { bgcolor: '#009ec5' }
                }}
              >
                View Station
              </Button>

              <Button 
                component={Link} 
                to="/" 
                variant="outlined" 
                size="large" 
                startIcon={<HomeIcon />}
                sx={{ 
                  borderRadius: 50, 
                  px: 4, 
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  borderColor: '#aaa',
                  color: '#666',
                  '&:hover': { borderColor: '#666', bgcolor: '#f5f5f5' }
                }}
              >
                Back Home
              </Button>

            </Stack>

          </Paper>
        </Fade>
      </Box>
    );
  }

  // --- FORM VIEW ---
  return (
    <Box sx={{ backgroundImage: `url(${sixthBg})`, backgroundSize: "cover", backgroundAttachment: "fixed", minHeight: "100vh", py: { xs: 0, md: 8 }, px: { xs: 0, md: 4 } }}>
      <Container maxWidth="md" disableGutters>
        <Fade in={!loading} timeout={800}>
          <Stack spacing={2}>
            
            {/* --- STATION INFO --- */}
            <Card sx={{ 
              borderRadius: { xs: 0, md: 5 }, 
              overflow: 'hidden', 
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative',
              mx: { xs: 0, md: 0 },
              border: 'none'
            }}>
              <Box sx={{ position: 'relative', width: '100%', bgcolor: '#000' }}>
                <CardMedia
                  component="img"
                  image={station.thumbnail || station.mainImage}
                  alt={station.location}
                  sx={{ 
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: '80vh', 
                    objectFit: 'contain'
                  }}
                />
                
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)',
                  p: 3,
                  pt: 8,
                }}>
                  <Typography 
                    variant="h2" 
                    component="h1"
                    fontWeight="800" 
                    sx={{ 
                        color: 'white', 
                        fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                        lineHeight: 1.1,
                        textShadow: '3px 3px 6px rgba(0,0,0,0.9)'
                    }}
                  >
                    {station.location}
                  </Typography>

                  {station.province && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#4dd0e1', mt: 1 }}>
                      <LocationOnIcon sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' } }} />
                      <Typography variant="h5" fontWeight="700" sx={{ letterSpacing: 1.5, textTransform: 'uppercase', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                          {station.province}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Box>
            </Card>

            {/* --- FORM --- */}
            <Paper sx={{ 
                p: { xs: 3, md: 6 }, 
                borderRadius: { xs: 3, md: 3 }, 
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                mx: { xs: 1, md: 0 },
                mt: -2,
                position: 'relative',
                zIndex: 2
            }}>
              
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="900" sx={{ color: '#00b5e2', letterSpacing: 1 }}>
                  WE VALUE YOUR FEEDBACK
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, fontSize: '1rem' }}>
                  Please tell us about your experience so we can serve you better.
                </Typography>
              </Box>

              <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2}>
                  
                  <Grid item xs={12}>{renderTextField("name", "Full Name", <PersonIcon />)}</Grid>
                  <Grid item xs={12}>{renderTextField("email", "Email Address", <EmailIcon />)}</Grid>
                  <Grid item xs={12}>{renderTextField("contactNumber", "Contact Number", <PhoneIcon />)}</Grid>
                  <Grid item xs={12}>{renderTextField("message", "Share your thoughts...", null, true)}</Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }}>
                      <Typography variant="caption" sx={{ color: '#999', px: 1, letterSpacing: 1 }}>RATINGS</Typography>
                    </Divider>
                    
                    <Grid container spacing={1} justifyContent="center">
                      <Grid item xs={6} md={3}>{renderRating("Experience", "experience")}</Grid>
                      <Grid item xs={6} md={3}>{renderRating("Facilities", "facilities")}</Grid>
                      <Grid item xs={6} md={3}>{renderRating("Service", "service")}</Grid>
                      <Grid item xs={6} md={3}>{renderRating("Cleanliness", "cleanliness")}</Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ alignItems: 'flex-start', ml: -0.5, mt: 1 }} 
                      control={
                        <Checkbox 
                          checked={formData.consent} 
                          onChange={handleChange} 
                          name="consent" 
                          sx={{ p: 1, color: errors.consent ? 'red' : '#00b5e2', '&.Mui-checked': { color: '#00b5e2' } }} 
                        />
                      }
                      label={
                        <Typography variant="caption" color={errors.consent ? "error" : "text.secondary"} sx={{ fontSize: '0.85rem' }}>
                          I consent to the use of my testimonial. Your data is confidential and will be handled in accordance with our privacy policy.
                        </Typography>
                      }
                    />
                  </Grid>

                  {generalError && (
                    <Grid item xs={12}>
                        <Alert severity="error" sx={{ py: 0, px: 2 }}>{generalError}</Alert>
                    </Grid>
                  )}

                  <Grid item xs={12} display="flex" justifyContent="center" sx={{ mt: 2 }}>
                    <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth sx={{ backgroundColor: "#00C4F0", borderRadius: 50, fontWeight: "800", boxShadow: "0 4px 14px rgba(0, 196, 240, 0.4)" }}>
                      {submitting ? <CircularProgress size={24} color="inherit" /> : "Submit Feedback"}
                    </Button>
                  </Grid>

                </Grid>
              </form>
            </Paper>
          </Stack>
        </Fade>
      </Container>
    </Box>
  );
};

export default Feedback;